import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Analytics } from '@/utils/analytics';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { Buffer } from 'buffer';
import { Button } from '@/components/button';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import {
  CUSTOM_TAG_FIELD_MAX_LENGTH,
  getCustomTagFieldsForTemplate,
  renderZPLTemplate,
  ZPLData,
} from '@/utils/tag-printing';
import { FormInputContainer } from '@/components/form';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { PageContainer } from '@/components/page-container';
import { SCREEN_PADDING_SMALL, TYPOGRAPHY } from '@/styles';
import { StackScreenProps } from '@react-navigation/stack';
import { TagPrintConfirmation } from '@/components/tag-print-confirmation';
import { TextInput } from '@/components/text-input';
import { Type } from '@/components/toast';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useBluetoothContext } from '@/contexts/bluetooth-context';
import { usePrintTagTemplates } from '@/hooks/use-print-tag-templates';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';

type Props = StackScreenProps<
  AppContainerStackParams,
  APP_CONTAINER_ROUTES.CustomTagPrint
>;

/**
 * Prints a single shelf tag from hand-entered values instead of a catalog
 * item. The tag is rendered on device from the template's text template — the
 * same rendering used when printing a batch offline — because the server side
 * render requires a variant pack to pull values from.
 */
export const CustomTagPrintScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { printTagTemplateUUID } = route.params;
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { clearSelectedDevice, selectedDevice } = useBluetoothContext();
  const { showErrorToast, showToast } = useToasts();
  const { bottom } = useSafeAreaInsets();
  const [styles] = useStyles();

  const [fieldValues, setFieldValues] = useState<ZPLData>({});
  const [showPrinterSelection, setShowPrinterSelection] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // Shares the picker's resolution of which templates are usable, so the two
  // cannot drift on where templates are read from or which ones qualify.
  const { error, loading, refetch, templates } = usePrintTagTemplates({
    requireLocalRendering: true,
  });

  const printerTextTemplate = templates.find(
    ({ uuid }) => uuid === printTagTemplateUUID,
  )?.offline_printer_text_template;

  const fields = useMemo(
    () =>
      printerTextTemplate
        ? getCustomTagFieldsForTemplate(printerTextTemplate)
        : [],
    [printerTextTemplate],
  );

  const hasValues = fields.some(({ key }) => Boolean(fieldValues[key]));

  const onPrint = useCallback(async () => {
    if (!(selectedDevice && (await selectedDevice.isConnected()))) {
      clearSelectedDevice();
      showErrorToast(i18n.t('bluetooth_device.disconnected'));
      return;
    }

    if (!printerTextTemplate) {
      showErrorToast();
      return;
    }

    setIsPrinting(true);
    try {
      const printBuffer = Buffer.from(
        renderZPLTemplate(printerTextTemplate, fieldValues),
      ).toString('base64');
      await selectedDevice.write(printBuffer, 'base64');
      Analytics.track('custom_tag_print.submit_button.press', {
        deviceName: selectedDevice.name,
        printTagTemplateUUID,
        restaurantUUID,
        supplierUUID,
      });
      setShowPrinterSelection(false);
      showToast({
        autoCloseTimeout: 3000,
        content: i18n.t('batch_print_tags.sent_singular'),
        type: Type.POSITIVE,
      });
    } catch (e) {
      captureException(e);
      showErrorToast();
    } finally {
      setIsPrinting(false);
    }
  }, [
    clearSelectedDevice,
    fieldValues,
    printerTextTemplate,
    printTagTemplateUUID,
    restaurantUUID,
    selectedDevice,
    showErrorToast,
    showToast,
    supplierUUID,
  ]);

  /**
   * Only prompts for a printer when there isn't a connected one, matching the
   * other print flows where the printer is chosen once and later presses print
   * straight away.
   */
  const onPrintPress = useCallback(async () => {
    if (selectedDevice && (await selectedDevice.isConnected())) {
      await onPrint();
      return;
    }

    setShowPrinterSelection(true);
  }, [onPrint, selectedDevice]);

  return (
    <PageContainer
      navigationHeaderProps={{
        BackButtonIcon: ArrowLeftThin,
        centerTitle: true,
        onBackPress: navigation.goBack,
        showBackButton: true,
        showShadow: false,
        title: i18n.t('custom_tag'),
      }}
    >
      <KeyboardAvoidingView
        behavior={Platform.select({ android: undefined, ios: 'padding' })}
        style={styles.base}
      >
        {loading ? (
          <ActivityIndicator style={styles.loadingContainer} />
        ) : fields.length > 0 ? (
          <>
            <ScrollView
              contentContainerStyle={styles.form}
              keyboardShouldPersistTaps="handled"
              style={styles.scroll}
            >
              <Text style={[TYPOGRAPHY.p, styles.messageText]}>
                {i18n.t('custom_tag.message')}
              </Text>
              {fields.map(({ key, labelKey }) => (
                <FormInputContainer key={key}>
                  <TextInput
                    containerStyle={styles.input}
                    label={i18n.t(labelKey)}
                    maxLength={CUSTOM_TAG_FIELD_MAX_LENGTH}
                    onChangeText={(text) =>
                      setFieldValues((current) => ({ ...current, [key]: text }))
                    }
                    showDoneButtonAccessoryView
                    testID={`custom-tag-input-${key}`}
                    value={fieldValues[key] ?? ''}
                  />
                </FormInputContainer>
              ))}
            </ScrollView>
            <View
              style={{ paddingBottom: Math.max(bottom, SCREEN_PADDING_SMALL) }}
            >
              <Button
                disabled={!hasValues || isPrinting}
                loading={isPrinting}
                onPress={onPrintPress}
                trackingData={{
                  name: 'custom_tag_print.print_button',
                  properties: {
                    printTagTemplateUUID,
                  },
                }}
              >
                {i18n.t('print_tag')}
              </Button>
            </View>
          </>
        ) : error ? (
          <View style={styles.emptyState}>
            <Text style={[TYPOGRAPHY.medium, styles.messageText]}>
              {i18n.t('custom_tag.load_error')}
            </Text>
            <Button
              onPress={() => refetch()}
              style={styles.retryButton}
              trackingData={{
                name: 'custom_tag_print.retry_button',
                properties: {
                  printTagTemplateUUID,
                },
              }}
              type="secondary"
            >
              {i18n.t('retry')}
            </Button>
          </View>
        ) : (
          <View style={styles.emptyState}>
            <Text style={[TYPOGRAPHY.medium, styles.messageText]}>
              {i18n.t('custom_tag.template_unavailable')}
            </Text>
          </View>
        )}
        {/* Kept inside the form branch so an offline flip mid-flow cannot
            leave the sheet floating over the error or unavailable state. */}
        {fields.length > 0 && showPrinterSelection ? (
          <TagPrintConfirmation
            isInstantPrint={false}
            loadingTemplates={isPrinting}
            onClose={() => setShowPrinterSelection(false)}
            onPrint={onPrint}
            onPrinterSelect={() => {}}
            setIsInstantPrint={() => {}}
            tagCount={1}
          />
        ) : null}
      </KeyboardAvoidingView>
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  emptyState: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  form: {
    paddingBottom: 24,
  },
  input: {
    borderColor: theme.colors.divider,
    borderWidth: 1,
  },
  loadingContainer: {
    marginTop: 24,
  },
  messageText: {
    color: theme.sk.color.text.secondary,
    lineHeight: 22,
  },
  retryButton: {
    marginTop: 16,
  },
  scroll: {
    flex: 1,
  },
}));
